"use client";

import BulkImportArticle from "../../../components/BulkImportArticle";

const data = {
  title: "Bulk Import — Opening Balances",
  audience: "For: Anyone setting starting balances for existing customers & vendors",
  intro: [
    "Upload opening balances (starting amounts owed) for customers and vendors that already exist, per branch, from an Excel/CSV-style sheet. Each row ties one customer/vendor to one branch.",
  ],
  createsOnlyNote:
    "This tool does not create customers, vendors, or branches. It only sets a starting balance. If the customer/vendor doesn't exist yet, set it up first.",
  steps: {
    commitVerb: "Apply the balances",
    commitLabel: "Apply",
    allOrNothing:
      "Applying is all-or-nothing: if even one row has a problem, nothing is recorded — the whole batch is rolled back. Check first.",
  },
  calloutsAfterSteps: [
    {
      type: "warning",
      text: "One prerequisite is only caught at the final step, not at Check: each customer/vendor must already have a receivable/payable account assigned. If one is missing, the whole Apply fails with a message naming that customer — even if the Check step passed. Make sure accounts are assigned before you Apply.",
    },
  ],
  requiredFields: [
    { field: "Customer Number", needs: "The number of a customer/vendor that already exists (exact match). Not created here." },
    { field: "Branch Name", needs: "The name of an existing branch (see the matching note below)." },
    { field: "Opening Balance", needs: "The starting amount. Up to 2 decimal places. Can be positive or negative." },
  ],
  conditionalFields: [
    { field: "Sign (positive / negative)", rule: "For a customer, a positive amount means they owe you. For a vendor, a positive amount means you owe them (a payable). A negative amount is the reverse (a credit)." },
    { field: "A 0 or blank balance", rule: "Always accepted — treated as 'nothing to do' and never conflicts with anything on record." },
    { field: "Decimals", rule: "At most 2 decimal places." },
  ],
  matchFields: [
    { field: "Customer Number", rule: "Must exactly match an existing customer/vendor number (case-sensitive)." },
    { field: "Branch Name", rule: "Matched against your company's branches by name, case-insensitively. If the branch isn't already attached to the customer, it is attached when you Apply." },
  ],
  matchNote:
    "Customer Number must match exactly. Branch Name is matched against existing company branches (case-insensitive); note it is matched company-wide rather than strictly under that one customer.",
  duplicateRules: [
    {
      condition: "That branch already has an opening balance on record",
      result: "Rejected — you can only set a branch's opening balance once.",
    },
    {
      condition: "That branch already has activity (its current balance isn't zero)",
      result: "Rejected — once a branch has moved past its starting point, an opening balance can't be set.",
    },
    {
      condition: "The same Customer + Branch appears twice in your sheet",
      result: "Rejected — list each customer/branch pairing only once per upload.",
    },
    {
      condition: "Opening Balance is 0 or blank",
      result: "Always accepted — never conflicts with anything already on record.",
    },
  ],
  mistakes: [
    { problem: "Customer Number missing or doesn't match", meaning: "The customer/vendor must already exist — check the number, or add it first." },
    { problem: "Branch Name missing or doesn't match", meaning: "The branch must already exist in the company — check spelling." },
    { problem: "Opening Balance isn't a valid number or has >2 decimals", meaning: "Enter a plain number (positive or negative), rounded to 2 decimals." },
    { problem: "Branch already has an opening balance or has activity", meaning: "You can set an opening balance only once, before any activity — these are caught at Check." },
    { problem: "Same customer + branch appears twice in the sheet", meaning: "List each pairing only once." },
    { problem: "Customer has no receivable/payable account", meaning: "Assign one first — this is caught only at Apply and fails the whole upload, naming the customer." },
  ],
  onApply: [
    "For each non-zero row, if the branch wasn't already attached to the customer, it is added along with its opening balance.",
    "The opening balance becomes that branch's starting point — both its 'opening' figure and its 'current' figure begin at the amount you entered.",
    "An accounting entry is posted to the customer's receivable (or vendor's payable) ledger, so the balance is reflected in your accounts from day one, with the offset to an opening-balance adjustment.",
    "The whole batch is written together in a single transaction — if anything fails, nothing is saved.",
  ],
  workflow: [
    "Make sure each customer/vendor already has a receivable/payable account assigned.",
    "Build your sheet with customer numbers, branch names, and the opening balance for each.",
    "Run the Check step first and review the problem rows.",
    "Fix each issue and re-check until every row passes.",
    "Only then run Apply to record the opening balances.",
    "Tip: if someone else imports a balance for the same customer and branch between your Check and Apply, your row can still be rejected at the last moment — just check again before retrying.",
  ],
};

export default function BulkOpeningBalancePage() {
  return <BulkImportArticle data={data} />;
}
